import { motion } from 'framer-motion';
import { FiArrowDownCircle } from 'react-icons/fi';
import { useEffect, useState } from 'react';


const roles = [
  'Software Engineer',
  'Java Developer',
  'Full Stack Developer',
  'Backend Developer',
  'Open-Source Contributor'
];

function TypingAnimation() {
  const [displayedText, setDisplayedText] = useState('');
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    let timeout;
    const currentRole = roles[currentRoleIndex];
    if (isTyping) {
      if (displayedText.length < currentRole.length) timeout = setTimeout(() => setDisplayedText(currentRole.substring(0, displayedText.length + 1)), 100);
      else timeout = setTimeout(() => { setIsTyping(false); setIsDeleting(true); }, 1500);
    } else if (isDeleting) {
      if (displayedText.length > 0) timeout = setTimeout(() => setDisplayedText(displayedText.substring(0, displayedText.length - 1)), 50);
      else { setIsDeleting(false); setCurrentRoleIndex((currentRoleIndex + 1) % roles.length); setIsTyping(true); }
    }
    return () => clearTimeout(timeout);
  }, [displayedText, currentRoleIndex, isTyping, isDeleting]);
  return (
    <span className="text-indigo-600 dark:text-indigo-400">
      {displayedText}
      <motion.span animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 0.8 }} className="ml-1">|</motion.span>
    </span>
  );
}

export default function Hero({ heroRef, yBg, scrollToSection }) {
  return (
        <section
          id="home"
          ref={heroRef}
          className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-4 pb-12 pt-24 text-center sm:min-h-screen sm:px-6"
        >
          <motion.div
            className="absolute inset-0 -z-10"
            style={{
              y: yBg,
              background: 'radial-gradient(circle, rgba(79,70,229,0.1) 0%, rgba(79,70,229,0) 100%)'
            }}
          />



          <motion.h1
            className="mb-5 max-w-5xl text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold leading-[1.08] text-gray-900 dark:text-white sm:mb-6"
            initial={{ opacity: 0, y: -60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, type: "spring" }}
          >
            Hi, I'm <span className="text-indigo-600 dark:text-indigo-400">Shambhavi Singh</span>
          </motion.h1>

          <motion.h2
            className="mb-6 min-h-8 text-xl font-bold text-gray-700 dark:text-gray-300 sm:mb-8 sm:min-h-10 sm:text-3xl md:text-4xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            I am <TypingAnimation />
          </motion.h2>

          <motion.div
            className="mx-auto mb-4 max-w-2xl text-base text-gray-700 dark:text-gray-300 sm:mb-5 sm:text-lg md:text-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <p className="hero-subtext">
              MCA graduate and Software Engineer with <strong>2 production-deployed applications</strong> — an enterprise railway reservation platform (Spring Boot, JWT, Docker, CI/CD) and a freelance physiotherapy e-commerce platform (Next.js, Node.js, Razorpay, PostgreSQL RLS).
              Experienced across the full SDLC in both internship and independent project work.
            </p>
          </motion.div>
          <motion.div
            className="mx-auto mb-5 max-w-2xl text-base text-gray-700 dark:text-gray-300 sm:mb-6 sm:text-lg md:text-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <div className="hero-badges">
              <span>Problem Solver</span> |
              <span> <strong>Clear Communicator</strong></span> |
              <span> Fluent: English & Hindi</span>
            </div>
          </motion.div>

          <motion.div
            className="flex w-full max-w-xs flex-col gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <motion.a
              href="https://drive.google.com/file/d/1JGXUfr-hFseBgZn0DHE0UGye03mqUvFE/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white shadow transition hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 sm:w-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Check Resume
            </motion.a>
            <motion.button
              onClick={() => scrollToSection("projects")}
              className="w-full rounded-lg border border-indigo-600 px-6 py-3 font-semibold text-indigo-600 transition hover:bg-indigo-50 dark:border-indigo-400 dark:text-indigo-400 dark:hover:bg-gray-800 sm:w-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Projects
            </motion.button>
          </motion.div>

          <motion.button
            type="button"
            aria-label="Scroll to About section"
            className="mt-6 cursor-pointer text-3xl text-indigo-600 dark:text-indigo-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            onClick={() => scrollToSection("about")}
            whileHover={{ scale: 1.2 }}
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <FiArrowDownCircle aria-hidden="true" />
            </motion.div>
          </motion.button>
        </section>
  );
}
